import React, { useState, useEffect } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  styled,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import UniComplyLogo from "@icons/UniComplyLogoSmall";
import { useNavigate, useLocation } from "react-router-dom";
import { logoutLocal } from "@services/auth";
import { getCountries, Country } from "@services/countries";
import { sendSupportEmail } from "@services/SendEmail";
import { initializeCountriesCache, setSelectedCountryCode } from "@utils/countryUtils";
import { canViewByName, canAddByName, FEATURE_NAMES } from "@utils/permissions";
import { UserFacility } from "@services/facilityManagement";
import {
  canPreviewOtherVerticals,
  getCurrentUserId,
  isGlobalAdministrator,
  userCanUseNetworkFacility,
} from "@utils/userUtils";
import VerticalPreviewSwitcher from "@components/Spaces/VerticalPreviewSwitcher";
import {
  getMyVerticalContext,
  getPreviewVertical,
  getRequestVerticalOverride,
} from "@services/verticals";
import { billingService } from "@services/billing";
import { joinPublicPath } from "@utils/publicPath";
import { NetworkRailIcon } from "./facilityNetwork/NetworkNavIcons";
import NotificationBell from "./NotificationBell";
const NavigationContainer = styled(Box)({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between", // pushes user section to bottom
});

const SidebarContainer = styled(Box)(({ theme }) => ({
  width: "60px",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "8px",
  padding: "12px 4px",
  overflow: "hidden",
  // Sit above temporary MuiDrawer/Modal (default z-index 1300) so left-rail
  // Network / Facilities / Home stay clickable while the companion drawer is open.
  // Paper is offset with ml (~80px) so it does not cover this 60px rail.
  position: "relative",
  zIndex: theme.zIndex.modal + 1,
  flexShrink: 0,
}));

const LogoContainer = styled(Box)({
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  "& svg": {
    width: "48px",
    height: "auto",
    display: "block",
  },
});

const AddButton = styled(Box)({
  width: "48px",
  height: "48px",
  border: "none",
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  flexShrink: 0,
  "&:hover": {
    backgroundColor: "rgba(6, 91, 112, 0.08)",
  },
});

const AddIcon = styled(Box)({
  width: "32px",
  height: "32px",
  flexShrink: 0,
  backgroundColor: "transparent",
});

const NavItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$isActive",
})<{ $isActive?: boolean }>(({ $isActive }) => ({
  width: "60px",
  padding: "8px",
  borderRadius: "6px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "8px",
  cursor: "pointer",
  position: "relative",
  flexShrink: 0,
  backgroundColor: $isActive ? "#E4FAFC" : "transparent",
  "&::before": $isActive
    ? {
        content: '""',
        position: "absolute",
        left: 0,
        top: "8px",
        bottom: "8px",
        width: "2px",
        borderRadius: "999px",
        backgroundColor: "#0A829D",
      }
    : undefined,
  "&:hover": {
    backgroundColor: $isActive ? "#E4FAFC" : "#fff",
  },
}));

const NavIcon = styled(Box)({
  width: "20px",
  height: "20px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const NavLabel = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "22px",
  letterSpacing: "-0.4px",
  color: "#065b70",
  textAlign: "center",
});

const UserSection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "8px",
  width: "100%",
});
const UserSectionContainer = styled(Box)({
  display: "flex",
  padding: "8px 4px",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: "4px",
  alignSelf: "stretch",
});
const Avatar = styled(Box)({
  width: "32px",
  height: "32px",
  borderRadius: "50%",
  // backgroundColor: "#d1f0f7",
  backgroundColor: "transparent",
  border: "4px solid #0A829D",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "12px",
  fontWeight: "bold",
  // color: "#065b70",
});

const ActionButton = styled(Box)({
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  borderRadius: "4px",
  "&:hover": {
    backgroundColor: "rgba(6, 91, 112, 0.08)",
  },
});

interface SpacesNavigationProps {
  activeNavItem?: string;
  onNavItemClick?: (itemId: string) => void;
  userFacilities?: UserFacility[];
}

export default function SpacesNavigation({
  activeNavItem = "spaces",
  onNavItemClick,
  userFacilities = [],
}: SpacesNavigationProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuItems, setMenuItems] = useState<
    {
      label: string;
      path?: string;
      icon?: React.ElementType;
      countryCode?: string;
    }[]
  >([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>("USA");
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportScreen, setSupportScreen] = useState("");
  const [supportDesc, setSupportDesc] = useState("");
  const [supportSubmitting, setSupportSubmitting] = useState(false);
  const [supportMessage, setSupportMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const getFlagAssetPathForCountryCode = (codeInput?: string) => {
    const code = (codeInput || "USA").toUpperCase();
    if (code === "IND" || code === "IN" || code === "INDIA") {
      return joinPublicPath("assets/images/Flag_of_India.svg");
    }
    if (
      code === "ARE" ||
      code === "AE" ||
      code === "UAE" ||
      code === "UNITED ARAB EMIRATES"
    ) {
      return joinPublicPath("assets/images/Flag_of_the_United_Arab_Emirates.svg");
    }
    if (code === "CAN" || code === "CA" || code === "CANADA") {
      return joinPublicPath("assets/images/Flag_of_Canada.svg");
    }
    return joinPublicPath("assets/images/Flag_of_the_United_States.svg");
  };
  const getFlagSrc = () => getFlagAssetPathForCountryCode(selectedCountry);
  const getFlagSrcFromCode = (codeInput?: string) =>
    getFlagAssetPathForCountryCode(codeInput);
  const FlagIcon = () => (
    <img
      src={getFlagSrc()}
      alt="Country Selector"
      style={{
        width: 20,
        height: 20,
        borderRadius: "50%",
        objectFit: "cover",
        border: "1px solid #99D4E1",
      }}
    />
  );

  // Load countries on component mount and set default to USA
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const response = await getCountries();
        setCountries(response.countries);

        // Initialize the cache for countryUtils
        await initializeCountriesCache();

        // Check if there's a saved country preference, otherwise default to USA
        const savedCountry = localStorage.getItem("selectedCountry");
        if (savedCountry) {
          setSelectedCountry(savedCountry);
        } else {
          setSelectedCountry("USA");
          localStorage.setItem("selectedCountry", "USA");
        }
      } catch (error) {
        console.error("Failed to load countries:", error);
        // Default to USA even on error
        setSelectedCountry("USA");
        localStorage.setItem("selectedCountry", "USA");
      }
    };
    loadCountries();
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLElement>, type: string) => {
    if (type === "user") {
      const menuItemsList: { label: string; path: string }[] = [
        { label: "Profile", path: "/dashboard/profile" },
      ];

      // Check permissions (by feature name – resilient to ID changes)
      const canViewUserMgmt = canViewByName(FEATURE_NAMES.USER_MANAGEMENT);
      const canViewBillings = canViewByName(FEATURE_NAMES.BILLINGS);
      const canViewAuditLogs = canViewByName(FEATURE_NAMES.AUDIT_LOGS);
      const canViewLearningCenter = canViewByName(FEATURE_NAMES.LEARNING_CENTER);
      // Add User Management if user has view permission
      if (canViewUserMgmt) {
        menuItemsList.push({
          label: "User Management",
          path: "/usermanagement",
        });
      }

      // Add Billings if user has view permission
      if (canViewBillings) {
        menuItemsList.push({ label: "Payments & Billing", path: "/dashboard/billing" });
      }

      // Add Audit Logs if user has view permission
      if (canViewAuditLogs) {
        menuItemsList.push({
          label: "Audit Logs",
          path: "/dashboard/audit-logs",
        });
      }

      if (canViewLearningCenter) {
        menuItemsList.push({
          label: "Learning Center",
          path: "/dashboard/training-videos",
        });
      }

      if (canViewByName(FEATURE_NAMES.AGREEMENTS)) {
        menuItemsList.push({
          label: "Agreements",
          path: "/dashboard/agreements/acceptance",
        });
      }

      setMenuItems(menuItemsList);
    } else if (type === "flag") {
      // Dynamically populate countries from API
      const countryMenuItems = countries.map((country) => ({
        label: country.country_name,
        countryCode: country.country_code,
      }));
      setMenuItems(
        countryMenuItems.length > 0
          ? countryMenuItems
          : [{ label: "Loading countries...", icon: FlagIcon }],
      );
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleMenuClick = (item: {
    label: string;
    path?: string;
    countryCode?: string;
  }) => {
    // Handle country selection
    if (item.countryCode) {
      setSelectedCountry(item.countryCode);
      setSelectedCountryCode(item.countryCode);
      handleClose();
      return;
    }

    // if (item.path) {
    //   navigate(item.path); // navigate if path exists
    // }
    if (item.label === "Profile" || item.path === "/dashboard/profile") {
      navigate("/dashboard/profile");
      handleClose();
      return;
    }
    if (item.path === "/changepassword") {
      navigate("/dashboard/changepassword");
      handleClose();
      return;
    }
    if (item.path === "/usermanagement") {
      navigate("/dashboard/usermanagement");
      handleClose();
      return;
    }
    if (item.path === "/dashboard/billing" || item.label === "Billings") {
      navigate("/dashboard/billing");
      handleClose();
      return;
    }
    if (item.path === "/dashboard/audit-logs" || item.label === "Audit Logs") {
      navigate("/dashboard/audit-logs");
      handleClose();
      return;
    }
    if (
      item.path === "/dashboard/training-videos" ||
      item.label === "Learning center"
    ) {
      navigate("/dashboard/training-videos");
      handleClose();
      return;
    }
    if (
      item.path === "/dashboard/agreements" ||
      item.path === "/dashboard/agreements/acceptance" ||
      item.label === "Agreements"
    ) {
      navigate("/dashboard/agreements/acceptance");
      handleClose();
      return;
    }
  };

  const handleNavClick = (itemId: string) => {
    if (onNavItemClick) {
      onNavItemClick(itemId);
    }
  };

  const canAddFacility = canAddByName(FEATURE_NAMES.ADD_FACILITY) || isGlobalAdministrator();
  const canShowNetwork = userCanUseNetworkFacility();

  const handleAdd = async () => {
    // Check if user has permission to add facilities
    if (!canAddFacility) {
      console.warn(
        "⚠️ [SpacesNavigation] User does not have permission to add facilities",
      );
      return;
    }
    try {
      const { getCompanySetupStatus } = await import("@services/companySetup");
      const status = await getCompanySetupStatus();
      if (!status.has_company) {
        // Route through Dashboard company→facility gate
        navigate("/dashboard", {
          state: { showMyFacilityDialog: true },
        });
        return;
      }
    } catch {
      // Status unknown — continue to facility setup
    }
    // Prefetch MCM trial gate so FacilityValidation can cap cart / verification.
    const userId = Number(getCurrentUserId() || "0");
    if (userId) {
      try {
        await billingService.getTrialFacilityGate(userId);
      } catch {
        // Validation screen re-fetches; fail closed there if still unknown.
      }
    }
    const overrideVertical = getRequestVerticalOverride() || getPreviewVertical();
    let preselectedVertical = overrideVertical || "healthcare";
    if (!overrideVertical) {
      try {
        const context = await getMyVerticalContext();
        preselectedVertical =
          context.effective_vertical || context.user_vertical || "healthcare";
      } catch {
        // Keep the legacy healthcare flow if the vertical context cannot load.
      }
    }
    navigate("/dashboard/MyFacility", {
      state: {
        currentView: "facilityValidation",
        reset: true,
        preselectedVertical,
      },
    });
  };
  const handleSignOut = async () => {
    try {
      await logoutLocal();
    } catch (_) {
      // ignore errors from logging/cleanup and still force redirect
    }

    // Use a hard redirect so production always lands on the public home,
    // even if SPA routing state is inconsistent.
    const target =
      process.env.REACT_APP_POST_LOGOUT_REDIRECT_URL || window.location.origin;
    window.location.replace(target);
  };

  const [userInitials, setUserInitials] = React.useState("?");

  const computeUserInitials = React.useCallback(() => {
    try {
      const raw = localStorage.getItem("user");
      if (!raw) return "?";
      const user = JSON.parse(raw) as Record<string, unknown>;
      const firstName =
        (user["first_name"] as string) || (user["firstName"] as string) || "";
      const lastName =
        (user["last_name"] as string) || (user["lastName"] as string) || "";
      const displayName =
        (user["display_name"] as string) ||
        (user["displayName"] as string) ||
        "";
      const email = (user["email"] as string) || "";
      try {
        sessionStorage.setItem("UserEmail", email);
      } catch (_) {}
      const fromNames = `${firstName} ${lastName}`.trim();
      const source = fromNames || displayName || email;
      if (!source) return "?";
      const parts = source.split(/[\s._-]+/).filter(Boolean);
      const first = parts[0]?.[0] || "";
      const second = parts.length > 1 ? parts[1]?.[0] : "";
      const initials = `${first}${second}`.toUpperCase();
      return initials || "?";
    } catch {
      return "?";
    }
  }, []);

  React.useEffect(() => {
    setUserInitials(computeUserInitials());
    const handleUserUpdated = () => setUserInitials(computeUserInitials());
    window.addEventListener(
      "unicomply:user-updated",
      handleUserUpdated as EventListener,
    );
    window.addEventListener("storage", handleUserUpdated);
    return () => {
      window.removeEventListener(
        "unicomply:user-updated",
        handleUserUpdated as EventListener,
      );
      window.removeEventListener("storage", handleUserUpdated);
    };
  }, [computeUserInitials]);

  const open = Boolean(anchorEl);
  
  return (
    <NavigationContainer>
      <SidebarContainer data-testid="dashboard-nav-rail">
        {/* Logo Section */}
        <LogoContainer>
          <UniComplyLogo />
        </LogoContainer>

        {/* Add Button - Only show if user has permission to add facilities */}
        {canAddFacility && (
          <AddButton onClick={handleAdd} role="button" aria-label="Add facility">
            <AddIcon>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
              >
                <path
                  d="M16 30C23.732 30 30 23.732 30 16C30 8.26801 23.732 2 16 2C8.26801 2 2 8.26801 2 16C2 23.732 8.26801 30 16 30ZM15 10C15 9.44771 15.4477 9 16 9C16.5523 9 17 9.44772 17 10V15H22C22.5523 15 23 15.4477 23 16C23 16.5523 22.5523 17 22 17H17V22C17 22.5523 16.5523 23 16 23C15.4477 23 15 22.5523 15 22V17H10C9.44771 17 9 16.5523 9 16C9 15.4477 9.44772 15 10 15H15V10Z"
                  fill="#065B70"
                />
              </svg>
            </AddIcon>
          </AddButton>
        )}

        {/* Navigation Items */}
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1 }}
        >
          {/* <NavItem
            isActive={activeNavItem === "home"}
            onClick={() => handleNavClick("home")}
          >
            <NavIcon>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M8.99655 2.38506C9.56701 1.87165 10.433 1.87165 11.0034 2.38506L16.5034 7.33506C16.8195 7.61952 17 8.02477 17 8.45V15.5046C17 16.3331 16.3284 17.0046 15.5 17.0046H4.5C3.67157 17.0046 3 16.3331 3 15.5046V8.45C3 8.02477 3.18048 7.61952 3.49655 7.33506L8.99655 2.38506ZM10.3345 3.12835C10.1443 2.95722 9.85567 2.95722 9.66552 3.12835L4.16552 8.07835C4.06016 8.17317 4 8.30826 4 8.45V15.5046C4 15.7808 4.22386 16.0046 4.5 16.0046H15.5C15.7761 16.0046 16 15.7808 16 15.5046V8.45C16 8.30826 15.9398 8.17317 15.8345 8.07835L10.3345 3.12835Z"
                  fill="#065B70"
                />
              </svg>
            </NavIcon>
            <NavLabel>Home</NavLabel>
          </NavItem> */}

          {canViewByName(FEATURE_NAMES.DISCOVER) && (
            <NavItem
              $isActive={activeNavItem === "discover"}
              onClick={() => handleNavClick("discover")}
            >
              <NavIcon>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18C12.0289 18 13.8814 17.2447 15.2916 16H10V17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3H13.876C12.7278 2.36284 11.4063 2 10 2ZM10 4H15.2916C15.6375 4.30521 15.9567 4.63985 16.2454 5H10V4ZM16.9297 6H10V7H17.4185C17.2784 6.65398 17.1148 6.31998 16.9297 6ZM10 8H17.748C17.8318 8.32577 17.8957 8.65956 17.9381 9H10V8ZM18 10H10V11H17.9381C17.979 10.6724 18 10.3387 18 10ZM10 12H17.748C17.6597 12.3428 17.5494 12.6766 17.4185 13H10V12ZM16.9297 14H10V15H16.2454C16.4977 14.6853 16.7267 14.3511 16.9297 14Z"
                    fill="#065B70"
                  />
                </svg>
              </NavIcon>
              <NavLabel>Discover</NavLabel>
            </NavItem>
          )}

          <NavItem
            $isActive={activeNavItem === "spaces"}
            onClick={() => handleNavClick("spaces")}
          >
            <NavIcon>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M4.5 2C3.11929 2 2 3.11929 2 4.5V12.5C2 13.8807 3.11929 15 4.5 15H12.5C13.8807 15 15 13.8807 15 12.5V4.5C15 3.11929 13.8807 2 12.5 2H4.5ZM14 4.5V5H7.5C7.22386 5 7 4.77614 7 4.5V3H12.5C13.3284 3 14 3.67157 14 4.5ZM6 4.5C6 5.32843 6.67157 6 7.5 6H14V12.5C14 13.3284 13.3284 14 12.5 14H4.5C3.67157 14 3 13.3284 3 12.5V4.5C3 3.67157 3.67157 3 4.5 3H6V4.5ZM7.4998 18C6.29032 18 5.28144 17.1411 5.0498 16H6.08515C6.29107 16.5826 6.84669 17 7.4998 17H13.9998C15.6566 17 16.9998 15.6569 16.9998 14V7.5C16.9998 6.84689 16.5824 6.29127 15.9998 6.08535V5.05001C17.1409 5.28165 17.9998 6.29052 17.9998 7.5V14C17.9998 16.2091 16.2089 18 13.9998 18H7.4998Z"
                  fill="#065B70"
                />
              </svg>
            </NavIcon>
            <NavLabel>Facilities</NavLabel>
          </NavItem>

          {canShowNetwork && (
            <NavItem
              $isActive={activeNavItem === "network"}
              onClick={() => handleNavClick("network")}
              role="button"
              aria-label="Network"
            >
              <NavIcon>
                <NetworkRailIcon />
              </NavIcon>
              <NavLabel>Network</NavLabel>
            </NavItem>
          )}
        </Box>

        {/* User Section */}
        <UserSection>
          <UserSectionContainer
            onClick={(e) => handleClick(e, "user")}
            sx={{ cursor: "pointer" }}
          >
            <Avatar> {userInitials}</Avatar>
          </UserSectionContainer>
          <UserSectionContainer
            onClick={(e) => handleClick(e, "flag")}
            sx={{ cursor: "pointer" }}
          >
            <img
              src={getFlagSrc()}
              alt="Country Selector"
              style={{
                width: "24px",
                height: "24px",
                borderRadius: "50%",
                objectFit: "cover",
                border: "1px solid #99D4E1",
              }}
            />
            <Typography
              sx={{
                fontSize: "8px",
                fontWeight: 700,
                color: "#065B70",
                backgroundColor: "#E6FAFC",
                padding: "1px 3px",
                borderRadius: "2px",
                lineHeight: 1,
                textAlign: "center",
              }}
            >
              {selectedCountry}
            </Typography>
          </UserSectionContainer>
          {/* Executive/global-admin vertical preview switcher.
              Placed directly under the country selector per product spec so the
              two scope-controls (geography + vertical) sit together in the
              left-anchored drawer. */}
          {canPreviewOtherVerticals() && <VerticalPreviewSwitcher />}
          <NotificationBell
            active={location.pathname.startsWith("/dashboard/notifications")}
          />
          {/* Support icon */}
          <UserSectionContainer
            sx={{ cursor: "pointer" }}
            onClick={() => setSupportOpen(true)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M12 2C6.486 2 2 6.037 2 10.917C2 13.681 3.507 16.154 5.938 17.739V22L10.09 19.838C10.709 19.943 11.348 20 12 20C17.514 20 22 15.963 22 11.083C22 6.204 17.514 2 12 2Z"
                stroke="#065B70"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="12" cy="11" r="1.5" fill="#065B70" />
            </svg>
          </UserSectionContainer>
          {/* <UserSectionContainer>
            <ActionButton>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="17"
                viewBox="0 0 16 17"
                fill="none"
              >
                <path
                  d="M11.5 4.5C12.0523 4.5 12.5 4.05228 12.5 3.5C12.5 2.94772 12.0523 2.5 11.5 2.5C10.9477 2.5 10.5 2.94772 10.5 3.5C10.5 4.05228 10.9477 4.5 11.5 4.5ZM7.20711 0.585786C7.58218 0.210714 8.09089 0 8.62132 0H13.0732C14.1778 0 15.0732 0.89543 15.0732 2V6.37426C15.0732 6.90818 14.8598 7.41993 14.4803 7.79556L8.66241 13.5553C7.88023 14.3297 6.61938 14.3265 5.8411 13.5482L1.45711 9.16421C0.676058 8.38317 0.676058 7.11684 1.45711 6.33579L7.20711 0.585786ZM8.62132 1C8.3561 1 8.10175 1.10536 7.91421 1.29289L2.16421 7.04289C1.77369 7.43342 1.77369 8.06658 2.16421 8.45711L6.54821 12.8411C6.93735 13.2302 7.56777 13.2318 7.95886 12.8446L13.7768 7.08491C13.9665 6.89709 14.0732 6.64122 14.0732 6.37426V2C14.0732 1.44772 13.6255 1 13.0732 1H8.62132ZM0.997088 9.80009C0.731977 10.5111 0.885242 11.3427 1.45688 11.9143L4.43375 14.8912C5.9903 16.4478 8.512 16.4541 10.0764 14.9054L14.4801 10.5457C14.8595 10.1701 15.073 9.6583 15.073 9.12438V8.55225L10.0764 13.5053C10.0281 13.5531 9.97893 13.5994 9.92891 13.6442L9.37281 14.1947C8.19955 15.3563 6.30827 15.3515 5.14086 14.1841L4.64236 13.6856C4.5711 13.6237 4.50151 13.5589 4.43375 13.4911L1.45688 10.5143C1.24747 10.3049 1.09421 10.0606 0.997088 9.80009Z"
                  fill="#065B70"
                />
              </svg>
            </ActionButton>
          </UserSectionContainer> */}

          <UserSectionContainer>
            <ActionButton onClick={handleSignOut}>
              {
                /* <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M15 10H5M10 15l5-5-5-5"
                stroke="#065b70"
                strokeWidth="1.5"
              />
            </svg> */
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 32 32"
                  fill="none"
                >
                  <path
                    d="M18.5 23C18.7761 23 19 22.7761 19 22.5C19 22.2239 18.7761 22 18.5 22H12C10.8954 22 10 21.1046 10 20V12C10 10.8954 10.8954 10 12 10H18.5C18.7761 10 19 9.77614 19 9.5C19 9.22386 18.7761 9 18.5 9H12C10.3431 9 9 10.3431 9 12V20C9 21.6569 10.3431 23 12 23H18.5ZM19.6464 12.1464C19.8417 11.9512 20.1583 11.9512 20.3536 12.1464L23.8536 15.6464C24.0488 15.8417 24.0488 16.1583 23.8536 16.3536L20.3536 19.8536C20.1583 20.0488 19.8417 20.0488 19.6464 19.8536C19.4512 19.6583 19.4512 19.3417 19.6464 19.1464L22.2929 16.5H13.5C13.2239 16.5 13 16.2761 13 16C13 15.7239 13.2239 15.5 13.5 15.5H22.2929L19.6464 12.8536C19.4512 12.6583 19.4512 12.3417 19.6464 12.1464Z"
                    fill="#065B70"
                  />
                </svg>
              }
            </ActionButton>
          </UserSectionContainer>
        </UserSection>
      </SidebarContainer>
      <Dialog
        open={supportOpen}
        onClose={(event, reason) => {
          // Prevent closing on backdrop click or escape key
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          setSupportOpen(false);
          setSupportDesc("");
          setSupportScreen("");
          setSupportMessage(null);
        }}
        maxWidth={false}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.4)",
          },
        }}
        PaperProps={{
          sx: {
            width: "560px", // custom width
            height: "470px", // custom height
            borderRadius: "12px",
            border: "1px solid #CCF5FF",
            backgroundColor: "#fffdfd",
            boxShadow: "0px 4px 16px 0px rgba(5,89,108,0.08)",
            "@media (max-width: 640px)": {
              width: "90vw",
              height: "80vh",
            },
          },
        }}
      >
        <DialogTitle sx={{ p: 2, borderBottom: "1px solid #BFE5EC" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Rethink Sans", sans-serif',
                fontWeight: 600,
                fontSize: "18px",
                color: "#065B70",
              }}
            >
              Contact Support
            </Typography>
            <Button
              onClick={() => {
                setSupportOpen(false);
                setSupportDesc("");
                setSupportScreen("");
                setSupportMessage(null);
              }}
              sx={{
                minWidth: 0,
                p: 0,
                color: "#065B70",
                textTransform: "none",
              }}
            >
              ✕
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mt: 1,
            fontFamily: '"Rethink Sans", sans-serif',
          }}
        >
          {supportMessage && (
            <Box
              sx={{
                border:
                  supportMessage.type === "success"
                    ? "1px solid #34D399"
                    : "1px solid #FCA5A5",
                background:
                  supportMessage.type === "success" ? "#ECFDF5" : "#FEF2F2",
                color:
                  supportMessage.type === "success" ? "#065F46" : "#991B1B",
                borderRadius: "8px",
                px: 2,
                py: 1,
              }}
            >
              <Typography sx={{ fontSize: "12px" }}>
                {supportMessage.text}
              </Typography>
            </Box>
          )}
          <Typography sx={{ fontSize: "13px", color: "#737373" }}>
            We’re here to help you with any questions or issues
          </Typography>
          <FormControl fullWidth size="small">
            <Typography sx={{ fontSize: "12px", color: "#737373", mb: "14px" }}>
              Select an area of the platform you are having issues with{" "}
              <span style={{ color: "#dc2626" }}>*</span>
            </Typography>
            <Select
              value={supportScreen}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <span style={{ color: "#99A3A9" }}>Select an area...</span>
                  );
                }
                return selected as string;
              }}
              onChange={(e) => setSupportScreen(e.target.value as string)}
              sx={{
                background: "#FDFFFF",
                border: "1px solid #99D4E1",
                borderRadius: "8px",
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              }}
            >
              <MenuItem value="" disabled>
                <em>Select an area...</em>
              </MenuItem>
              <MenuItem value="My Facility">My Facility</MenuItem>
              <MenuItem value="Regulations Library">Regulations Library</MenuItem>
              <MenuItem value="Discover">Discover</MenuItem>
              <MenuItem value="Regulations">Regulations</MenuItem>
              <MenuItem value="User Management">User Management</MenuItem>
              <MenuItem value="Pricing">Pricing</MenuItem>
              <MenuItem value="Billings">Billings</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
          <Typography sx={{ fontSize: "12px", color: "#737373", mb: 0.5 }}>
            Description <span style={{ color: "#dc2626" }}>*</span>
          </Typography>
          <TextField
            placeholder="Describe the issue or question..."
            multiline
            minRows={4}
            value={supportDesc}
            onChange={(e) => setSupportDesc(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#99D4E1" },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#99D4E1",
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setSupportOpen(false);
              setSupportDesc("");
              setSupportScreen("");
              setSupportMessage(null);
            }}
            sx={{
              background: "#FFFFFF",
              border: "1px solid #B3B3B3",
              borderRadius: "8px",
              px: 3,
              textTransform: "none",
              color: "#262626",
              "&:hover": { background: "#f5f5f5" },
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={
              !supportDesc.trim() || !supportScreen || supportSubmitting
            }
            onClick={async () => {
              try {
                setSupportSubmitting(true);
                const raw = localStorage.getItem("user");
                const user = raw ? JSON.parse(raw) : {};
                const name =
                  user?.first_name || user?.firstName
                    ? `${user.first_name || user.firstName} ${
                        user.last_name || user.lastName || ""
                      }`.trim()
                    : user.display_name || user.displayName || "User";
                const email = user?.email || "";
                await sendSupportEmail({
                  screen_name: supportScreen,
                  description: supportDesc,
                  user_name: name,
                  user_email: email,
                  user_number: "",
                  to_email: "support@affablebpm.com",
                  subject: `Support Request – ${supportScreen}`,
                });
                setSupportMessage({
                  type: "success",
                  text: "Your message has been sent successfully.",
                });
                setTimeout(() => {
                  setSupportOpen(false);
                  setSupportDesc("");
                  setSupportScreen("");
                  setSupportMessage(null);
                }, 1000);
              } catch (e) {
                console.error("Support email failed", e);
                setSupportMessage({
                  type: "error",
                  text: "Failed to send. Please try again later.",
                });
              } finally {
                setSupportSubmitting(false);
              }
            }}
            sx={{
              background: "linear-gradient(90deg, #0A829D 0%, #58C2D8 100%)",
              boxShadow: "0 2px 8px 0 rgba(0, 0, 0, 0.25)",
              borderRadius: "8px",
              px: 3,
              textTransform: "none",
              color: "#e6fafc",
              "&:hover": {
                opacity: 0.9,
                background: "linear-gradient(90deg, #0A829D 0%, #58C2D8 100%)",
              },
            }}
          >
            {supportSubmitting ? "Sending..." : "Send"}
          </Button>
        </DialogActions>
      </Dialog>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        slotProps={{
          root: {
            sx: {
              // Rail uses modal+1 so drawers stay behind it. Avatar/country menus
              // must sit above the rail or Discover/Facilities paint through the popup.
              zIndex: (theme) => theme.zIndex.modal + 2,
            },
          },
        }}
        PaperProps={{
          sx: {
            display: "flex",
            width: 178,
            p: 1, // 8px
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 0.5, // 4px
            borderRadius: "8px",
            border: "1px solid #5bbfd6",
            backgroundColor: "#FFF",
            boxShadow: "0 3px 8.1px 0 rgba(111, 211, 232, 0.2)",
            ml: "40px",
            mt: "30px",
          },
        }}
      >
        <List sx={{ width: "100%" }}>
          {menuItems.map((item) => (
            <ListItem
              key={item.label}
              disablePadding
              sx={{
                width: "100%",
                padding: "0px",
                "&:hover": {
                  backgroundColor: "#E6FAFC", // light blue matching application theme
                },
              }}
            >
              <ListItemButton
                onClick={() => handleMenuClick(item)}
                selected={item.countryCode === selectedCountry}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  // width: "100%",
                  "&.Mui-selected": {
                    backgroundColor: "#CCF3FA", // darker blue for selected state
                    "&:hover": {
                      backgroundColor: "#B8EDF7", // even darker on hover when selected
                    },
                  },
                  "&:hover": {
                    backgroundColor: "#E6FAFC", // light blue for regular hover
                  },
                }}
              >
                {/* Flag icon only for country items */}
                {item.countryCode && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mr: 1,
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={getFlagSrcFromCode(item.countryCode)}
                      alt={item.label}
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: "1px solid #99D4E1",
                      }}
                    />
                  </Box>
                )}

                {/* Label */}
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    sx: {
                      fontSize: 14,
                      color: "#262626",
                      width: "100%",
                      flex: 1,
                      m: 0,
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Popover>
    </NavigationContainer>
  );
}
